import { FC, useMemo } from "react";

import { SessionStatsCard } from "./SessionStatsCard";

import { t } from "i18n/i18n";
import { ClassCollaborationIcon, MicrophoneIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectCurrentSectionId,
  selectExtendedClassSessionSummariesForDateRange,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { computeStudentTalkTimeStats } from "utils/metrics-utils";

type StudentTalkTimeCardProps = {
  dateRangeValue: number;
};

export const StudentTalkTimeCard: FC<StudentTalkTimeCardProps> = ({ dateRangeValue }) => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const classSessions = useListSelector(
    (s) => selectExtendedClassSessionSummariesForDateRange(s, dateRangeValue),
    ["student_talk_time_seconds", "instructor_talk_time_seconds", "started_at"],
  );

  const { canAuthorSection } = useCapabilities();

  const { averageStudentTalkTime, highestStudentTalkTime } = useMemo(
    () => computeStudentTalkTimeStats(classSessions),
    [classSessions],
  );

  const isEmpty = classSessions.length === 0;

  if (!canAuthorSection || !sectionId) return null;

  const highestText = highestStudentTalkTime.date
    ? t("session_log_view.stats.student_talk_time.highest", highestStudentTalkTime)
    : t("session_log_view.stats.student_talk_time.no_sessions");

  return (
    <SessionStatsCard
      FooterIcon={ClassCollaborationIcon}
      footerText={highestText}
      isEmpty={isEmpty}
      kind="student_talk_time"
      title={t("session_log_view.stats.student_talk_time.title")}
      TitleIcon={MicrophoneIcon}
      value={`${averageStudentTalkTime}%`}
      valueDescription={t("session_log_view.stats.student_talk_time.value_description")}
    />
  );
};
